import { useState, useCallback, useMemo } from "react";
import {
  T, SC, FONT, s, m,
  CARD, CARD_INNER, SECTION_LABEL, VALUE_LARGE, VALUE_MEDIUM,
  LABEL_SM, LABEL_XS, TH_STYLE, TD_STYLE, INPUT_STYLE,
  BAR_TRACK, TAB_BASE, chip, dot, STAT_CARD,
} from "../theme/playbookTheme";
import OrbitalVisualizer from "./OrbitalVisualizer";

// ── Physics constants ─────────────────────────────────────────
const DEF_PHYS = {
  mu:3.986004418e14, RE:6371000, g0:9.80665,
  J2:1.08263e-3, SRP:4.56e-6, Cd:2.2,
  rho_ref:4.71e-13, H:48000, alt_ref:500000,
  B_equator:3.0e-5, solar_flux:1361, panel_eff:0.295,
  al_density:2700, ti_density:4430,
};

const DEF_MISSION = {
  name:"SPACEMANCRAFT-1", org:"Spacemancraft Pvt Ltd",
  satClass:"12U", mass:12, alt:450, inc:97.5, raan:45,
  years:3, isp:220, tier:"SKM4",
  payload:"EO Camera", payloadMass:1.2, payloadPower:4.5,
};

// ── Physics engine ────────────────────────────────────────────
function calcOrbit(p, ms) {
  const r = p.RE + ms.alt * 1000;
  const v = Math.sqrt(p.mu / r);
  const T_orb = 2 * Math.PI * r / v;
  const rho = p.rho_ref * Math.exp(-(ms.alt * 1000 - p.alt_ref) / p.H);
  const area = ms.satClass==="3U"?0.01:ms.satClass==="6U"?0.02:
               ms.satClass==="12U"?0.03:ms.satClass==="27U"?0.06:0.10;
  const Fd = 0.5 * p.Cd * area * rho * v * v;
  const ve = p.g0 * ms.isp;
  const dv_intrack = Fd * 3.156e7 * ms.years / ms.mass;
  const dv_NS = 48.6 * ms.years;
  const dv_total = dv_intrack + dv_NS;
  const prop_conv = ms.mass * (dv_total / ve) * 1.923;
  const prop_ep   = ms.mass * (dv_total / ve) * 1.020 * 0.90 * 0.92;
  const saving_pct = (prop_conv - prop_ep) / prop_conv * 100;
  const revs_day = 86400 / T_orb;
  const gs_day = revs_day * 2 * Math.PI * p.RE / 1e6;
  const eclipse = T_orb * 0.385;
  const sun_time = T_orb - eclipse;
  const srp_force = p.SRP * area;
  const J2_precession = -1.5 * Math.sqrt(p.mu) * p.J2 * Math.pow(p.RE,2) /
    Math.pow(r,3.5) * Math.cos(ms.inc * Math.PI/180) * 86400 * (180/Math.PI);
  return { r, v, T_orb, rho, area, Fd, ve, dv_intrack, dv_NS, dv_total,
           prop_conv, prop_ep, saving_pct, revs_day, gs_day, eclipse,
           sun_time, srp_force, J2_precession };
}

function calcPower(ms, orb, p) {
  const solar_area = ms.satClass==="3U"?0.03:ms.satClass==="6U"?0.06:
                     ms.satClass==="12U"?0.12:ms.satClass==="27U"?0.24:0.40;
  const p_solar = p.solar_flux * solar_area * p.panel_eff * 0.85;
  const p_obc=1.2, p_comms_rx=0.5, p_comms_tx=4.0;
  const p_adcs=0.25, p_eps=0.3, p_thermal=1.0, p_prop=0.22;
  const p_payload = ms.payloadPower;
  const p_total_eclipse = p_obc + p_comms_rx + p_adcs + p_eps + p_thermal + p_prop + 0.5;
  const p_total_sun = p_total_eclipse + p_comms_tx + p_payload;
  const bat_capacity = (p_total_eclipse * orb.eclipse / 3600) / 0.3;
  const bat_mass = bat_capacity / 180;
  const solar_mass = solar_area * 0.8;
  return { p_solar, p_obc, p_comms_rx, p_comms_tx, p_adcs, p_eps,
           p_thermal, p_prop, p_payload, p_total_eclipse, p_total_sun,
           bat_capacity, bat_mass, solar_mass };
}

function buildBOM(ms, orb, pw) {
  const isSmall = ["3U","6U"].includes(ms.satClass);
  return {
    structure:[
      { id:"STR-001", subsys:"Structure", name:"Primary Chassis / Bus Frame",
        part: isSmall?"ISIS 6U CubeSat Structure":"ISIS 12U CubeSat Structure",
        vendor:"Innovative Solutions In Space (ISIS)", pn:"CS-CUBESAT-6U",
        qty:1, mass_g:300, power_mw:0, cost_usd:4500, spec:"PCB rails, PocketQube compatible" },
      { id:"STR-002", subsys:"Structure", name:"Side Panels (×4)",
        part:"Al 6061-T6 machined panel 95×95×2mm",
        vendor:"Custom / Pumpkin Inc", pn:"CUSTOM-PANEL-01",
        qty:4, mass_g:45, power_mw:0, cost_usd:200, spec:"Threaded M2.5 inserts" },
      { id:"STR-003", subsys:"Structure", name:"Top/Bottom Deck (×2)",
        part:"Al 6061-T6 machined plate 100×100×3mm",
        vendor:"Custom", pn:"CUSTOM-DECK-01",
        qty:2, mass_g:80, power_mw:0, cost_usd:150, spec:"Central bore for harness routing" },
      { id:"STR-004", subsys:"Structure", name:"Primary Fasteners M3×8 Ti Grade 5",
        part:"DIN 912 M3×8 Titanium", vendor:"Würth Group", pn:"WÜRTH-004912-300-8",
        qty:48, mass_g:0.4, power_mw:0, cost_usd:1.2, spec:"Torque 0.55 N·m, Loctite 222" },
      { id:"STR-005", subsys:"Structure", name:"M2.5×6 Socket Cap Screws",
        part:"DIN 912 M2.5×6 A4 Stainless", vendor:"Würth Group", pn:"WÜRTH-004912-250-6",
        qty:80, mass_g:0.2, power_mw:0, cost_usd:0.35, spec:"PCB standoffs, torque 0.28 N·m" },
      { id:"STR-006", subsys:"Structure", name:"M2×5 Micro Screws",
        part:"DIN 912 M2×5 A4 Stainless", vendor:"Bossard Group", pn:"BN-3099",
        qty:60, mass_g:0.08, power_mw:0, cost_usd:0.25, spec:"Sensor & connector retention" },
      { id:"STR-007", subsys:"Structure", name:"M3 Heli-Coil Thread Inserts",
        part:"Heli-Coil 1185-3CN250 1.5D", vendor:"Heli-Coil / Stanley Engineered Fastening",
        pn:"1185-3CN250", qty:48, mass_g:0.15, power_mw:0, cost_usd:0.80,
        spec:"Al chassis thread reinforcement" },
      { id:"STR-008", subsys:"Structure", name:"M2.5 PCB Standoffs",
        part:"Male-Female M2.5 Al Standoffs 5/8/11/15mm",
        vendor:"RAF Electronic Hardware", pn:"M2059",
        qty:40, mass_g:0.5, power_mw:0, cost_usd:0.60, spec:"PCB stack spacing" },
      { id:"STR-009", subsys:"Structure", name:"Vibration Isolator Grommets",
        part:"LORD Mounts VM-105 M3 rubber grommets",
        vendor:"LORD Corporation (Parker Hannifin)", pn:"VM-105",
        qty:16, mass_g:1.5, power_mw:0, cost_usd:4.50,
        spec:"RW and OBC isolation, −55°C to +125°C" },
      { id:"STR-010", subsys:"Structure", name:"Thermal Washers M3 Kapton",
        part:"Kapton M3 insulating washer 0.1mm",
        vendor:"Dupont / McMaster-Carr", pn:"MCM-95405A505",
        qty:32, mass_g:0.05, power_mw:0, cost_usd:0.15, spec:"Electrical isolation at mounts" },
      { id:"STR-011", subsys:"Structure", name:"Separation System",
        part:"ISL CubeSat Deployer Interface Plate",
        vendor:"ISIS / NanoRacks", pn:"NRCSD-PLATE-01",
        qty:1, mass_g:120, power_mw:0, cost_usd:800,
        spec:"PPOD/NRCSD compatible, spring loaded" },
    ],
    obc:[
      { id:"OBC-001", subsys:"OBC", name:"Primary Onboard Computer",
        part:"GomSpace NanoMind Z7000", vendor:"GomSpace", pn:"A3200-Z7000",
        qty:1, mass_g:90, power_mw:1200, cost_usd:8500,
        spec:"Xilinx Zynq-7000 SoC, ARM Cortex-A9 dual-core 866MHz, 512MB DDR3, 8GB eMMC" },
      { id:"OBC-002", subsys:"OBC", name:"Flight Software (OS)",
        part:"FreeRTOS + GomSpace libGS", vendor:"GomSpace / Open Source", pn:"SW-FREERTOS-GS",
        qty:1, mass_g:0, power_mw:0, cost_usd:0,
        spec:"RTOS kernel, CSP protocol, task scheduling, watchdog, FDIR" },
      { id:"OBC-003", subsys:"OBC", name:"Mass Memory Module",
        part:"GomSpace NanoMem 32GB", vendor:"GomSpace", pn:"NM-32GB-NAND",
        qty:1, mass_g:15, power_mw:150, cost_usd:1200,
        spec:"NAND flash 32GB, ECC protected, radiation tolerant to 20 krad" },
      { id:"OBC-004", subsys:"OBC", name:"Watchdog / Reset Circuit",
        part:"MAX706 / External WDT PCB", vendor:"Maxim / Custom PCB", pn:"MAX706RESA",
        qty:1, mass_g:5, power_mw:10, cost_usd:85,
        spec:"Independent reset, 1.6s timeout, SPI commanded" },
      { id:"OBC-005", subsys:"OBC", name:"Real Time Clock Module",
        part:"DS3231 precision RTC module", vendor:"Maxim Integrated (Analog Devices)", pn:"DS3231SN",
        qty:1, mass_g:3, power_mw:2, cost_usd:12,
        spec:"±2ppm accuracy, I2C, battery backup, temperature comp" },
      { id:"OBC-006", subsys:"OBC", name:"OBC PCB Custom",
        part:"6-layer FR4 100×80mm OBC PCB", vendor:"Custom / JLCPCB Space", pn:"CUSTOM-OBC-PCB-01",
        qty:1, mass_g:45, power_mw:0, cost_usd:350,
        spec:"IPC Class 3, conformal coated, −55°C to +85°C" },
    ],
    eps:[
      { id:"EPS-001", subsys:"EPS", name:"Power Management & Distribution Unit",
        part:"EnduroSat EPS Type III", vendor:"EnduroSat", pn:"EPS-III-V2",
        qty:1, mass_g:96, power_mw:300, cost_usd:4800,
        spec:"4 solar inputs, 3.3V/5V/12V regulated rails, 8A total" },
      { id:"EPS-002", subsys:"EPS", name:"Solar Panels — Deployable (+X/−X)",
        part:"EnduroSat Solar Panel 1U Deployable", vendor:"EnduroSat", pn:"SP-1U-DEPLOY",
        qty:2, mass_g:98, power_mw:0, cost_usd:2200,
        spec:"4× TJ solar cells, Vmp=4.8V, Isc=520mA, eff=29.5%" },
      { id:"EPS-003", subsys:"EPS", name:"Solar Panels — Body-mounted (+Y/−Y)",
        part:"DHV Technology 1U body panel", vendor:"DHV Technology", pn:"DHV-SP-1U-BM",
        qty:4, mass_g:35, power_mw:0, cost_usd:1100,
        spec:"2× TJ cells, 2.0W per face, back-contact cells" },
      { id:"EPS-004", subsys:"EPS", name:"Battery Pack",
        part:`GomSpace NanoPower BPX ${pw.bat_capacity.toFixed(0)}Wh`,
        vendor:"GomSpace", pn:"NP-BPX-40Wh",
        qty:1, mass_g:Math.round(pw.bat_mass*1000), power_mw:0, cost_usd:2800,
        spec:`Li-ion 18650, ${pw.bat_capacity.toFixed(1)}Wh, 7.4V nominal, PCM protection` },
      { id:"EPS-005", subsys:"EPS", name:"Battery Heater",
        part:"Minco Thermofoil HK5578R5.4L12A", vendor:"Minco Technology Labs", pn:"HK5578R5.4L12A",
        qty:2, mass_g:4, power_mw:500, cost_usd:45,
        spec:"5W heater strip, 0.1mm Kapton, −65°C to +200°C, PSA backing" },
      { id:"EPS-006", subsys:"EPS", name:"Shunt Regulator PCB",
        part:"Custom MPPT PCB", vendor:"Custom", pn:"CUSTOM-MPPT-01",
        qty:1, mass_g:25, power_mw:0, cost_usd:180,
        spec:"Peak power tracking, INA219 monitoring, I2C telemetry" },
      { id:"EPS-007", subsys:"EPS", name:"Power Connector (internal)",
        part:"Molex Micro-Fit 3.0 ×8 circuits", vendor:"Molex", pn:"43025-0800",
        qty:6, mass_g:4, power_mw:0, cost_usd:2.80,
        spec:"8A per circuit, 3A per pin, locking, gold-plated" },
    ],
    comms:[
      { id:"COM-001", subsys:"Comms", name:"UHF/VHF Transceiver (TT&C)",
        part:"ISIS TRXUV Half-duplex",
        vendor:"Innovative Solutions In Space (ISIS)", pn:"TRXUV-A",
        qty:1, mass_g:75, power_mw:4000, cost_usd:5500,
        spec:"UHF downlink 400–403MHz, VHF uplink 144–148MHz, AX.25/CSP, up to 38.4kbps" },
      { id:"COM-002", subsys:"Comms", name:"S-band Transmitter (payload data)",
        part:"Syrlinks EWC27", vendor:"Syrlinks", pn:"EWC27-S",
        qty:1, mass_g:150, power_mw:8000, cost_usd:18000,
        spec:"2200–2290 MHz, 1W RF, QPSK/OQPSK, up to 10 Mbps, AES-256" },
      { id:"COM-003", subsys:"Comms", name:"UHF Antenna — Deployable Dipole",
        part:"ISIS Deployable UHF Turnstile Antenna", vendor:"ISIS", pn:"ANT-UHF-TS",
        qty:1, mass_g:45, power_mw:0, cost_usd:850,
        spec:"Tape spring deployment, 0 dBi gain, SMA, burn wire mechanism" },
      { id:"COM-004", subsys:"Comms", name:"S-band Patch Antenna",
        part:"ASS S-band patch 2.25GHz",
        vendor:"Antenna Systems Solutions", pn:"ASS-SB-PATCH-01",
        qty:1, mass_g:28, power_mw:0, cost_usd:1200,
        spec:"RHCP, 6 dBi gain, 60° HPBW, embedded LNA option" },
      { id:"COM-005", subsys:"Comms", name:"RF Coaxial Cable U.FL → SMA",
        part:"Huber+Suhner MXSS-1.13 U.FL to SMA 15cm",
        vendor:"Huber+Suhner / Amphenol", pn:"MXSS-1.13-L150",
        qty:4, mass_g:8, power_mw:0, cost_usd:18,
        spec:"1.13mm coax, 0.3dB/15cm at 2.4GHz, space-grade" },
      { id:"COM-006", subsys:"Comms", name:"RF Switch / Diplexer",
        part:"Mini-Circuits ZFSWA2-63DR+", vendor:"Mini-Circuits", pn:"ZFSWA2-63DR+",
        qty:1, mass_g:12, power_mw:50, cost_usd:85,
        spec:"DC-6GHz SPDT switch, 50Ω, SMA, −55°C to +100°C" },
    ],
    adcs:[
      { id:"ADCS-001", subsys:"ADCS", name:"EP-ADCS EMMS Unit (4-axis SKM-4)",
        part:"Spacemancraft EMMS-4A (custom)",
        vendor:"Spacemancraft Pvt Ltd", pn:"SMC-EMMS-4A-V1",
        qty:4, mass_g:500, power_mw:220, cost_usd:15000,
        spec:"±32mm stroke, 800mN peak force, 10mN reset, 8Hz bandwidth, <5μrad residual jitter" },
      { id:"ADCS-002", subsys:"ADCS", name:"Cold Gas Thruster (EP-ADCS reset)",
        part:"VACCO Micro Propulsion System MiPS", vendor:"VACCO Industries", pn:"M18199-01",
        qty:1, mass_g:80, power_mw:200, cost_usd:22000,
        spec:"5mN nominal thrust, Isp=70s (N₂), 5g propellant load, 4× nozzles" },
      { id:"ADCS-003", subsys:"ADCS", name:"Star Tracker",
        part:"Berlin Space Technologies ST-200",
        vendor:"Berlin Space Technologies", pn:"BST-ST-200",
        qty:1, mass_g:290, power_mw:800, cost_usd:32000,
        spec:"<5 arcsec pointing, 8°×8° FOV, 2Hz update rate, −30°C to +60°C" },
      { id:"ADCS-004", subsys:"ADCS", name:"Magnetometer",
        part:"Billingsley TFM100G2",
        vendor:"Billingsley Aerospace & Defense", pn:"TFM100G2",
        qty:1, mass_g:38, power_mw:150, cost_usd:1800,
        spec:"±100μT range, <0.5nT noise, 3-axis, SPI" },
      { id:"ADCS-005", subsys:"ADCS", name:"Sun Sensors (×6 faces)",
        part:"Tensor Tech FSS-15 Fine Sun Sensor", vendor:"Tensor Tech Co.", pn:"FSS-15",
        qty:6, mass_g:6, power_mw:40, cost_usd:850,
        spec:"<0.1° accuracy, ±65° FOV, analog output, 2-axis" },
      { id:"ADCS-006", subsys:"ADCS", name:"GPS Receiver",
        part:"Novatel OEM719 Miniaturised GPS", vendor:"Novatel (Hexagon)", pn:"OEM719",
        qty:1, mass_g:18, power_mw:1100, cost_usd:3200,
        spec:"L1/L2 GPS+GLONASS, <1.5m CEP, UART/USB, −40°C to +85°C" },
      { id:"ADCS-007", subsys:"ADCS", name:"Inertial Measurement Unit",
        part:"Analog Devices ADIS16490", vendor:"Analog Devices", pn:"ADIS16490BSWZ",
        qty:1, mass_g:16, power_mw:130, cost_usd:420,
        spec:"6-DOF IMU, ±450°/s gyro, ±8g accel, SPI, vibration-compensated" },
    ],
    prop:[
      { id:"PROP-001", subsys:"Propulsion", name:"Cold Gas Tank (N₂)",
        part:`VACCO SF Cold Gas Tank ${(orb.prop_ep*1000+20).toFixed(0)}g cap`,
        vendor:"VACCO Industries", pn:"M18199-TANK",
        qty:1, mass_g:Math.round(orb.prop_ep*50+80), power_mw:0, cost_usd:3500,
        spec:`${(orb.prop_ep*1000).toFixed(0)}g N₂ capacity, COPV CFRP, 3000 psi MEOP` },
      { id:"PROP-002", subsys:"Propulsion", name:"Fill/Drain Valve",
        part:"VACCO Fill Drain Valve M18199-FDV", vendor:"VACCO Industries", pn:"M18199-FDV",
        qty:1, mass_g:18, power_mw:0, cost_usd:480, spec:"Schrader type, 1/8 NPT, 3000 psi rated" },
      { id:"PROP-003", subsys:"Propulsion", name:"Pressure Transducer",
        part:"Kulite XTEH-7L-190 miniature", vendor:"Kulite Semiconductor", pn:"XTEH-7L-190-1000A",
        qty:2, mass_g:12, power_mw:30, cost_usd:320, spec:"0–1000 psia, 0.1% FS accuracy, space-grade" },
      { id:"PROP-004", subsys:"Propulsion", name:"Propellant (N₂ gas)",
        part:`Nitrogen 99.9999% pure ${(orb.prop_ep*1000).toFixed(0)}g`,
        vendor:"Air Liquide / Linde Gas", pn:"N2-6N-FLIGHT",
        qty:1, mass_g:Math.round(orb.prop_ep*1000), power_mw:0, cost_usd:85,
        spec:"6N purity, flight-grade, pre-loaded at VACCO facility" },
      { id:"PROP-005", subsys:"Propulsion", name:"Thruster Valve Driver PCB",
        part:"Custom valve driver with MOSFETs", vendor:"Custom / Spacemancraft", pn:"SMC-VDR-01",
        qty:1, mass_g:12, power_mw:50, cost_usd:180,
        spec:"4-channel, 0–100% PWM, 24V drive, overcurrent protection" },
      { id:"PROP-006", subsys:"Propulsion", name:"EMMS Linear Track Assembly",
        part:"Spacemancraft EMMS Track Unit",
        vendor:"Spacemancraft Pvt Ltd / THK Co.", pn:"SMC-TRK-32MM",
        qty:4, mass_g:180, power_mw:0, cost_usd:2800,
        spec:"32mm stroke, THK linear guide, 1kg mass carriage, Ti end stops" },
      { id:"PROP-007", subsys:"Propulsion", name:"EMMS Electromagnet Coil",
        part:"Custom wound solenoid coil 800mN",
        vendor:"Custom / RS Components", pn:"SMC-COIL-800MN",
        qty:4, mass_g:95, power_mw:4, cost_usd:320,
        spec:"L=2mH, R=0.5Ω, τ=4ms, 800mN at full current" },
      { id:"PROP-008", subsys:"Propulsion", name:"EMMS Controller Board",
        part:"Spacemancraft EMMS Controller PCB",
        vendor:"Spacemancraft Pvt Ltd", pn:"SMC-CTRL-01",
        qty:1, mass_g:35, power_mw:120, cost_usd:850,
        spec:"STM32H7 MCU, 4-channel H-bridge, 50Hz control loop" },
    ],
    thermal:[
      { id:"THR-001", subsys:"Thermal", name:"MLI Blanket (exterior)",
        part:"Sheldahl MLI 15-layer blanket",
        vendor:"Sheldahl / Brentwood Industries", pn:"SHELD-MLI-15L",
        qty:1, mass_g:85, power_mw:0, cost_usd:650,
        spec:"15 layers double-aluminised Mylar + Dacron net spacers, ε<0.03" },
      { id:"THR-002", subsys:"Thermal", name:"Battery Heater Thermistor",
        part:"Variohm 10kΩ NTC type B57045-M103",
        vendor:"TDK / Variohm EuroSensor", pn:"B57045-M103-J",
        qty:8, mass_g:1, power_mw:0, cost_usd:2.50, spec:"10kΩ ±5%, −55°C to +150°C, glass bead" },
      { id:"THR-003", subsys:"Thermal", name:"Thermal Control Tape",
        part:"3M 850 Polyimide Film Tape", vendor:"3M Aerospace", pn:"3M-TAPE-850",
        qty:1, mass_g:20, power_mw:0, cost_usd:45, spec:"Al foil on Kapton, α=0.12, ε=0.04, 85°C rated" },
      { id:"THR-004", subsys:"Thermal", name:"Thermal Interface Material",
        part:"Bergquist GP3000 thermal pad", vendor:"Henkel / Bergquist", pn:"GP3000-0.010-00-04",
        qty:1, mass_g:10, power_mw:0, cost_usd:38, spec:"3 W/m·K, 0.25mm sheets, rework-capable" },
      { id:"THR-005", subsys:"Thermal", name:"Survival Heater PCB",
        part:"Minco Thermofoil silicone heater 3W",
        vendor:"Minco Technology Labs", pn:"HK5484R7.5L12A",
        qty:4, mass_g:3, power_mw:3000, cost_usd:55,
        spec:"3W per heater, PSA backing, operating to −65°C" },
      { id:"THR-006", subsys:"Thermal", name:"Black Paint (high-ε surfaces)",
        part:"AkzoNobel MAP space black paint", vendor:"AkzoNobel Aerospace", pn:"MAP-BLACK-06",
        qty:1, mass_g:0, power_mw:0, cost_usd:120,
        spec:"α=0.96, ε=0.88, −196°C to +250°C, MIL-C-46168 compliant" },
    ],
    payload:[
      { id:"PLD-001", subsys:"Payload", name:"EO Camera Module",
        part:"Simera Sense MultiScape100 CIS", vendor:"Simera Sense", pn:"MS100-CIS-V2",
        qty:1, mass_g:Math.round(ms.payloadMass*1000), power_mw:Math.round(ms.payloadPower*1000),
        cost_usd:45000, spec:"5m GSD from 450km, 12bit, 4000px swath, JPEG2000 onboard" },
      { id:"PLD-002", subsys:"Payload", name:"Payload Interface PCB",
        part:"Custom MIPI CSI-2 bridge", vendor:"Custom", pn:"CUSTOM-PLD-IF-01",
        qty:1, mass_g:22, power_mw:200, cost_usd:280,
        spec:"MIPI CSI-2 4-lane, FPGA-based formatter, LVDS output to OBC" },
      { id:"PLD-003", subsys:"Payload", name:"Payload Structural Mount",
        part:"Ti-6Al-4V machined bracket", vendor:"Custom", pn:"CUSTOM-PLD-MNT-01",
        qty:1, mass_g:45, power_mw:0, cost_usd:380,
        spec:"Kinematic mount, 3-point, M2.5 Ti screws" },
    ],
    harness:[
      { id:"HAR-001", subsys:"Harness", name:"Primary Power Bus Cable",
        part:"22AWG PTFE silver-plated copper", vendor:"Alpha Wire / Nexans", pn:"5857",
        qty:1, mass_g:65, power_mw:0, cost_usd:85,
        spec:"22AWG, 600V, −55°C to +200°C, PTFE insulation" },
      { id:"HAR-002", subsys:"Harness", name:"Signal Bus Cable (CAN/SPI/I2C)",
        part:"28AWG twisted pair PTFE", vendor:"Belden / Alpha Wire", pn:"BELDEN-9841",
        qty:1, mass_g:40, power_mw:0, cost_usd:55,
        spec:"28AWG, 300V, twisted pair, foil+braid shield" },
      { id:"HAR-003", subsys:"Harness", name:"Micro-D Connectors (15-pin)",
        part:"Glenair MicroD MDM-15 hermetic", vendor:"Glenair", pn:"MDM-15SH004A",
        qty:8, mass_g:6, power_mw:0, cost_usd:45,
        spec:"MIL-DTL-83513, 15-pin, gold contacts, hermetic seal" },
      { id:"HAR-004", subsys:"Harness", name:"PC-104 Bus Connectors",
        part:"Samtec SSW-120 stack connector", vendor:"Samtec", pn:"SSW-120-01-F-S",
        qty:6, mass_g:8, power_mw:0, cost_usd:12,
        spec:"PC-104 compliant, 2mm pitch, gold-plated, 1A per pin" },
      { id:"HAR-005", subsys:"Harness", name:"Crimp Tooling / Contacts",
        part:"Positronic Nano-Miniature gold contacts",
        vendor:"Positronic Industries", pn:"NS22M/30",
        qty:1, mass_g:5, power_mw:0, cost_usd:320,
        spec:"Size 22 contacts, gold plated, 28–24AWG" },
    ],
  };
}

function buildSequence(ms, orb) {
  const alt_park = ms.alt - 20;
  return [
    { phase:"PRE-LAUNCH", t:"T−365d to T−30d", color:SC.struct, title:"Assembly, Integration & Test",
      steps:["Mechanical assembly — chassis, PCBs, harness","Functional testing all subsystems",
        "Thermal vacuum test (−30°C to +70°C, 10⁻⁵ Torr, 72h)","Vibration test (sine 15g, random 14g RMS)",
        "EMC/EMI test (MIL-STD-461)","Mass properties final measurement",
        "Fill cold gas tank to flight load","Final inspection & close-out"] },
    { phase:"LAUNCH DAY", t:"T−4h to T+0", color:T.yel, title:"Launch Preparation & Lift-off",
      steps:["Battery charge verification >90% SoC","All subsystem health check nominal",
        "Separation switch armed","Installed in PPOD/NRCSD deployer",
        "Fairing close","LAUNCH VEHICLE LIFT-OFF",
        "Payload fairing separation","MECO / stage separation"] },
    { phase:"ORBIT INSERTION", t:"T+0 to T+90min", color:T.acc, title:"Separation & Initial Acquisition",
      steps:["SEPARATION from deployer (spring ejection ~2m/s ΔV)",
        "EP-ADCS EMMS powers ON — attitude stabilisation begins",
        "Solar panels deploy (burn wire activation T+30min)",
        "UHF beacon activated (CW morse ID)",
        "First contact with ground station (T+90min first pass)",
        "Initial telemetry dump — all subsystem health",
        "Battery state of charge check","GPS first fix — orbital elements determined"] },
    { phase:"COMMISSIONING", t:"T+1d to T+14d", color:SC.comms, title:"Subsystem Checkout",
      steps:["OBC software nominal — FDIR enabled","EPS power balance confirmed",
        "EP-ADCS EMMS calibration sequence (50 cycles)",
        "Star tracker first light — attitude solution confirmed",
        "Magnetometer calibration manoeuvre (B-field mapping)",
        "Cold gas thruster first fire (0.5mNs test pulse, T+7d)",
        "S-band transmitter first test — downlink confirmed",
        "Payload camera first image (T+10d)",
        "Ground station tracking confirmed >3 stations",
        "Propellant budget baseline — tank pressure nominal"] },
    { phase:"ORBIT RAISING", t:"T+14d to T+21d", color:SC.prop, title:`Drift to Operational Orbit ${ms.alt}km`,
      steps:[`Current parking orbit: ${alt_park}km × ${alt_park}km`,
        `Target orbit: ${ms.alt}km × ${ms.alt}km`,
        "EMMS holds attitude during each burn — zero desaturation needed",
        "Hohmann transfer — 2 burns 45 minutes apart",
        "Orbit determination after circularisation (GPS + OD filter)",
        "RAAN correction if needed (drag trim manoeuvre)",
        "Operational orbit confirmed — mission clock starts"] },
    { phase:"NOMINAL OPS", t:"T+21d to EOL", color:SC.payload, title:`Mission Operations — ${ms.years} Years`,
      steps:[`EP-ADCS station keeping every 133s — continuous`,
        `Daily ΔV budget: ${(orb.dv_intrack/365).toFixed(3)} m/s/day`,
        `Propellant consumed: ${(orb.prop_ep/ms.years/365*1000).toFixed(2)} g/day (EP-ADCS)`,
        "Imaging schedule uploaded 12h ahead via UHF",
        "S-band downlink payload data: 2× per day",
        "Weekly orbit determination update","Monthly star tracker recalibration",
        "Quarterly magnetometer calibration manoeuvre",
        `N-S station keeping: ${(48.6/365).toFixed(2)} m/s/day average`,
        "Annual thermal cycle check (±60°C range)",
        "OBC memory scrub weekly (SEU protection)"] },
    { phase:"EOL & DEORBIT", t:`EOL + 0 to EOL + ${ms.alt>500?25:3} years`, color:T.red, title:"End of Life Operations",
      steps:["Payload operations ceased","Final full telemetry archive downlinked",
        "Batteries safe mode (SOC <20%)","Passivation — remaining propellant vent",
        ms.alt>500
          ? `Natural decay from ${ms.alt}km — estimated ${ms.alt>600?50:25} years`
          : `Natural decay from ${ms.alt}km — estimated ${ms.alt>450?10:ms.alt>400?5:2} years`,
        "IADC 25-year rule compliance confirmed",
        "Transmitter final disable (ITU deregistration)"] },
  ];
}

// ═══════════════════════════════════════════════════════════════
//  UI PRIMITIVES
// ═══════════════════════════════════════════════════════════════

// Slider input row
const SI = ({ label, value, onChange, min, max, step=1, unit="" }) => (
  <div style={{ marginBottom: 18 }}>
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom: 6 }}>
      <span style={{ ...LABEL_SM }}>{label}</span>
      <span style={{ fontFamily: FONT.mono, fontSize: 13, fontWeight: 600, color: T.acc }}>
        {typeof value==="number" ? Number(value).toPrecision(4) : value}
        {unit && <span style={{ color: T.t2, fontSize: 11, marginLeft: 4 }}>{unit}</span>}
      </span>
    </div>
    <input type="range" min={min} max={max} step={step} value={value}
      onChange={e => onChange(Number(e.target.value))}
      style={{ width:"100%", accentColor: T.acc, height: 4, cursor:"pointer" }} />
    <div style={{ display:"flex", justifyContent:"space-between", marginTop: 3 }}>
      <span style={{ ...LABEL_XS }}>{min}</span>
      <span style={{ ...LABEL_XS }}>{max}</span>
    </div>
  </div>
);

// Progress bar gauge
const Gauge = ({ label, value, max, color, unit }) => {
  const pct = Math.min((value / max) * 100, 100);
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom: 5 }}>
        <span style={{ ...LABEL_SM }}>{label}</span>
        <span style={{ fontFamily: FONT.mono, fontSize: 12, fontWeight: 600, color }}>
          {typeof value==="number" ? value.toFixed(3) : value}
          {unit && <span style={{ color: T.t2, fontSize: 10, marginLeft: 4 }}>{unit}</span>}
        </span>
      </div>
      <div style={{ ...BAR_TRACK }}>
        <div style={{ height:"100%", width:`${pct}%`, background: color,
          borderRadius: 3, transition:"width 0.35s ease" }} />
      </div>
    </div>
  );
};

// Metric card — label + big value
const MetricCard = ({ label, value, unit, color=T.acc, sub }) => (
  <div style={{ ...CARD_INNER, borderLeft:`3px solid ${color}` }}>
    <div style={{ ...LABEL_XS, marginBottom: 4 }}>{label}</div>
    <div style={{ fontFamily: FONT.mono, fontSize: 16, fontWeight: 700, color, lineHeight: 1 }}>
      {value}
      {unit && <span style={{ fontSize: 10, color: T.t2, marginLeft: 5, fontWeight: 400 }}>{unit}</span>}
    </div>
    {sub && <div style={{ ...LABEL_XS, marginTop: 4 }}>{sub}</div>}
  </div>
);

// Table header cell
const TH = ({ children, c }) => (
  <th style={{ ...TH_STYLE, color: c || TH_STYLE.color }}>{children}</th>
);

// Table data cell
const TD = ({ children, c, bold, mono: useMono }) => (
  <td style={{ ...TD_STYLE, color: c || T.t0, fontWeight: bold ? 600 : 400,
    fontFamily: useMono ? FONT.mono : FONT.sans }}>
    {children}
  </td>
);

// ── Tab definitions ───────────────────────────────────────────
const TABS = [
  { id:"mission",    icon:"🎯", label:"Mission" },
  { id:"orbit",      icon:"🌍", label:"Orbit 3D" },
  { id:"physics",    icon:"⚛",  label:"Physics" },
  { id:"propulsion", icon:"🚀", label:"EP-ADCS" },
  { id:"subsystems", icon:"🛰",  label:"Subsystems" },
  { id:"bom",        icon:"📋", label:"Full BOM" },
  { id:"sequence",   icon:"📅", label:"Sequence" },
  { id:"summary",    icon:"📊", label:"Summary" },
];

// ── Section heading helper ────────────────────────────────────
const SectionHead = ({ children, color = T.t1 }) => (
  <div style={{ ...SECTION_LABEL, color, borderBottom:`1px solid ${T.b1}`, paddingBottom: 10, marginBottom: 20 }}>
    {children}
  </div>
);

// ── Divider row in spec lists ─────────────────────────────────
const SpecRow = ({ label, value, sub, color = T.t0 }) => (
  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start",
    padding:"8px 0", borderBottom:`1px solid ${T.b0}` }}>
    <div>
      <div style={{ ...LABEL_SM }}>{label}</div>
      {sub && <div style={{ ...LABEL_XS, marginTop: 2 }}>{sub}</div>}
    </div>
    <div style={{ fontFamily: FONT.mono, fontSize: 12, fontWeight: 600, color, textAlign:"right" }}>{value}</div>
  </div>
);

// ═══════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
export default function SatellitePlaybook() {
  const [tab, setTab]         = useState("mission");
  const [phys, setPhys]       = useState(DEF_PHYS);
  const [miss, setMiss]       = useState(DEF_MISSION);
  const [bomFilter, setBomFilter] = useState("all");
  const [seqOpen, setSeqOpen] = useState(null);

  const setP = useCallback((k, v) => setPhys(p => ({ ...p, [k]: v })), []);
  const setM = useCallback((k, v) => setMiss(ms => ({ ...ms, [k]: v })), []);

  const orb = useMemo(() => calcOrbit(phys, miss), [phys, miss]);
  const pw  = useMemo(() => calcPower(miss, orb, phys), [miss, orb, phys]);
  const BOM = useMemo(() => buildBOM(miss, orb, pw), [miss, orb, pw]);
  const SEQ = useMemo(() => buildSequence(miss, orb), [miss, orb]);

  const allBOM    = Object.values(BOM).flat();
  const filtBOM   = bomFilter === "all" ? allBOM : allBOM.filter(b => b.subsys.toLowerCase() === bomFilter.toLowerCase());
  const totalMass = allBOM.reduce((s, b) => s + b.mass_g * b.qty, 0) / 1000;
  const totalCost = allBOM.reduce((s, b) => s + b.cost_usd * b.qty, 0);
  const totalPow  = allBOM.reduce((s, b) => s + b.power_mw * b.qty, 0) / 1000;
  const totalItems= allBOM.reduce((s, b) => s + b.qty, 0);

  const subColors = {
    Structure:SC.struct, OBC:SC.obc, EPS:SC.eps, Comms:SC.comms,
    ADCS:SC.adcs, Propulsion:SC.prop, Thermal:SC.thermal, Payload:SC.payload, Harness:SC.harness,
  };

  return (
    <div style={{ background: T.bg0, minHeight:"100vh", color: T.t0, fontFamily: FONT.sans }}>

      {/* ── HEADER ──────────────────────────────────────────── */}
      <div style={{ background: T.bg1, borderBottom:`1px solid ${T.b1}`,
        padding:"16px 28px", display:"flex", justifyContent:"space-between",
        alignItems:"center", position:"sticky", top:0, zIndex:100 }}>
        <div>
          <div style={{ fontFamily: FONT.sans, fontSize: 10, fontWeight: 600,
            letterSpacing:"0.12em", textTransform:"uppercase", color: T.t2, marginBottom: 3 }}>
            Spacemancraft · Mission Engineering
          </div>
          <div style={{ fontFamily: FONT.sans, fontSize: 20, fontWeight: 700, color: T.t0, lineHeight: 1 }}>
            {miss.name}
            <span style={{ fontFamily: FONT.mono, fontSize: 12, fontWeight: 400,
              color: T.t2, marginLeft: 14 }}>
              {miss.satClass} · {miss.alt} km · {miss.years} yr · Isp = {miss.isp} s
            </span>
          </div>
        </div>
        <div style={{ display:"flex", gap: 8 }}>
          {[
            { l:"Propellant", v:`${orb.prop_ep.toFixed(2)} kg`, c: T.grn },
            { l:"Total mass", v:`${totalMass.toFixed(1)} kg`,   c: T.yel },
            { l:"Peak power", v:`${pw.p_total_sun.toFixed(1)} W`, c: T.acc },
            { l:"Prop saving", v:`${orb.saving_pct.toFixed(0)}%`, c: T.grn },
          ].map(stat => (
            <div key={stat.l} style={{ ...STAT_CARD, textAlign:"right" }}>
              <div style={{ ...LABEL_XS, marginBottom: 2 }}>{stat.l}</div>
              <div style={{ fontFamily: FONT.mono, fontSize: 15, fontWeight: 700, color: stat.c }}>{stat.v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── TAB BAR ─────────────────────────────────────────── */}
      <div style={{ background: T.bg1, borderBottom:`1px solid ${T.b1}`,
        padding:"0 24px", display:"flex", gap: 0 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            ...TAB_BASE,
            borderBottom: `2px solid ${tab === t.id ? T.acc : "transparent"}`,
            color: tab === t.id ? T.t0 : T.t2,
            fontWeight: tab === t.id ? 600 : 400,
          }}>
            <span style={{ marginRight: 6, fontSize: 13 }}>{t.icon}</span>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── PAGE CONTENT ────────────────────────────────────── */}
      <div style={{ padding:"28px 28px", maxWidth: 1280, margin:"0 auto" }}>

        {/* ══ MISSION TAB ═══════════════════════════════════ */}
        {tab === "mission" && (
          <div style={{ display:"grid", gridTemplateColumns:"340px 1fr", gap: 20 }}>

            {/* Left column */}
            <div style={{ display:"flex", flexDirection:"column", gap: 16 }}>
              <div style={{ ...CARD }}>
                <SectionHead>Mission Identity</SectionHead>
                {[
                  { label:"Mission Name", key:"name", type:"text" },
                  { label:"Organisation", key:"org",  type:"text" },
                ].map(f => (
                  <div key={f.key} style={{ marginBottom: 14 }}>
                    <div style={{ ...LABEL_SM, marginBottom: 6 }}>{f.label}</div>
                    <input value={miss[f.key]} onChange={e => setM(f.key, e.target.value)}
                      style={{ ...INPUT_STYLE }} />
                  </div>
                ))}
                <div style={{ marginBottom: 14 }}>
                  <div style={{ ...LABEL_SM, marginBottom: 6 }}>Payload Type</div>
                  <select value={miss.payload} onChange={e => setM("payload", e.target.value)}
                    style={{ ...INPUT_STYLE }}>
                    {["EO Camera","Quantum Magnetometer","SAR Radar","Laser ISL Terminal",
                      "Technology Demo","IoT Gateway","AIS Receiver"].map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ ...CARD }}>
                <SectionHead>Satellite Class</SectionHead>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap: 8, marginBottom: 20 }}>
                  {[
                    { cls:"3U",  desc:"1 – 3 kg" },
                    { cls:"6U",  desc:"4 – 8 kg" },
                    { cls:"12U", desc:"8 – 14 kg" },
                    { cls:"27U", desc:"14 – 24 kg" },
                  ].map(({ cls, desc }) => {
                    const active = miss.satClass === cls;
                    return (
                      <button key={cls} onClick={() => setM("satClass", cls)} style={{
                        padding:"10px 8px", cursor:"pointer",
                        background: active ? `${T.acc}15` : T.bg2,
                        border:`1px solid ${active ? T.acc : T.b1}`,
                        color: active ? T.acc : T.t1, borderRadius: 6,
                        fontFamily: FONT.sans, fontWeight: active ? 600 : 400,
                        transition:"all 0.12s",
                      }}>
                        <div style={{ fontSize: 15, marginBottom: 2 }}>{cls}</div>
                        <div style={{ fontSize: 10, color: T.t2 }}>{desc}</div>
                      </button>
                    );
                  })}
                </div>
                <SI label="Satellite Mass" value={miss.mass} onChange={v => setM("mass",v)} min={1} max={24} step={0.5} unit="kg" />
                <SI label="Payload Mass"   value={miss.payloadMass} onChange={v => setM("payloadMass",v)} min={0.1} max={5} step={0.1} unit="kg" />
                <SI label="Payload Power"  value={miss.payloadPower} onChange={v => setM("payloadPower",v)} min={0.5} max={15} step={0.5} unit="W" />
              </div>
            </div>

            {/* Right column */}
            <div style={{ display:"flex", flexDirection:"column", gap: 16 }}>
              <div style={{ ...CARD }}>
                <SectionHead>Orbital Parameters</SectionHead>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap: 24 }}>
                  <div>
                    <SI label="Altitude"         value={miss.alt}   onChange={v => setM("alt",v)}   min={300} max={600} step={5}   unit="km"    />
                    <SI label="Inclination"      value={miss.inc}   onChange={v => setM("inc",v)}   min={0}   max={98}  step={0.5} unit="°"     />
                    <SI label="Mission Duration" value={miss.years} onChange={v => setM("years",v)} min={1}   max={7}   step={0.5} unit="years" />
                    <SI label="EP-ADCS Isp"      value={miss.isp}   onChange={v => setM("isp",v)}   min={60}  max={950} step={10}  unit="s"     />
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap: 8, alignContent:"start" }}>
                    {[
                      { l:"Orbital velocity", v:orb.v.toFixed(0),            u:"m/s",          c:T.acc  },
                      { l:"Orbital period",   v:(orb.T_orb/60).toFixed(1),   u:"min",          c:T.t0   },
                      { l:"Rev / day",        v:orb.revs_day.toFixed(1),      u:"rev",          c:T.t0   },
                      { l:"Drag force",       v:(orb.Fd*1e6).toFixed(2),      u:"μN",           c:T.yel  },
                      { l:"Eclipse",          v:(orb.eclipse/60).toFixed(1),  u:"min",          c:T.t1   },
                      { l:"Sun time",         v:(orb.sun_time/60).toFixed(1), u:"min",          c:T.yel  },
                      { l:"Atmos density",    v:(orb.rho*1e12).toFixed(2),    u:"×10⁻¹² kg/m³",c:T.t1   },
                      { l:"J2 precession",    v:orb.J2_precession.toFixed(3), u:"°/day",        c:T.t1   },
                    ].map(stat => (
                      <MetricCard key={stat.l} label={stat.l} value={stat.v} unit={stat.u} color={stat.c} />
                    ))}
                  </div>
                </div>
              </div>

              <div style={{ ...CARD }}>
                <SectionHead color={SC.prop}>EP-ADCS Station-Keeping Budget</SectionHead>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap: 8 }}>
                  {[
                    { l:"In-track ΔV",        v:orb.dv_intrack.toFixed(2),             u:"m/s", c:T.acc  },
                    { l:"N-S ΔV",             v:orb.dv_NS.toFixed(1),                  u:"m/s", c:T.acc  },
                    { l:"Total ΔV",           v:orb.dv_total.toFixed(2),               u:"m/s", c:T.yel  },
                    { l:"Propellant (conv.)", v:orb.prop_conv.toFixed(3),              u:"kg",  c:T.red  },
                    { l:"Propellant (EP-ADCS)",v:orb.prop_ep.toFixed(3),              u:"kg",  c:T.grn  },
                    { l:"Mass saving",        v:(orb.prop_conv-orb.prop_ep).toFixed(3),u:"kg",  c:T.grn  },
                    { l:"Saving",             v:orb.saving_pct.toFixed(1),             u:"%",   c:T.grn  },
                    { l:"Daily use",          v:(orb.prop_ep/miss.years/365*1000).toFixed(2),u:"g/day",c:T.t0},
                  ].map(stat => (
                    <MetricCard key={stat.l} label={stat.l} value={stat.v} unit={stat.u} color={stat.c} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ══ ORBIT 3D TAB ══════════════════════════════════ */}
        {tab === "orbit" && (
          <OrbitalVisualizer miss={miss} orb={orb} setM={setM} />
        )}

        {/* ══ PHYSICS TAB ═══════════════════════════════════ */}
        {tab === "physics" && (
          <div style={{ display:"grid", gridTemplateColumns:"310px 1fr", gap: 20 }}>
            <div style={{ display:"flex", flexDirection:"column", gap: 14 }}>
              {[
                { title:"Gravitational", color:T.acc, fields:[
                  { k:"mu",    l:"Gravitational Parameter μ", min:3.95e14, max:4.0e14, step:1e10,  u:"m³/s²" },
                  { k:"RE",    l:"Earth Radius",              min:6350000, max:6380000, step:1000,  u:"m"     },
                  { k:"g0",    l:"Standard Gravity g₀",      min:9.77,    max:9.84,    step:0.001, u:"m/s²"  },
                  { k:"J2",    l:"J2 Oblateness",            min:1.07e-3, max:1.09e-3, step:1e-6,  u:""      },
                ]},
                { title:"Atmosphere", color:T.yel, fields:[
                  { k:"Cd",      l:"Drag Coefficient Cd",  min:1.5,  max:3.0,  step:0.05,  u:""      },
                  { k:"rho_ref", l:"Density at 500 km",    min:3e-13,max:7e-13,step:1e-14, u:"kg/m³" },
                  { k:"H",       l:"Scale Height",          min:40000,max:60000,step:500,   u:"m"     },
                ]},
                { title:"Solar & Magnetic", color:T.pur, fields:[
                  { k:"SRP",       l:"Solar Radiation Pressure", min:4e-6, max:5e-6, step:1e-8,  u:"N/m²"   },
                  { k:"solar_flux",l:"Solar Flux (AM0)",         min:1320, max:1420, step:5,     u:"W/m²"   },
                  { k:"panel_eff", l:"Solar Panel Efficiency",   min:0.25, max:0.32, step:0.005, u:""       },
                ]},
              ].map(group => (
                <div key={group.title} style={{ ...CARD, borderTop:`2px solid ${group.color}50`, padding:"18px 20px" }}>
                  <SectionHead color={group.color}>{group.title}</SectionHead>
                  {group.fields.map(f => (
                    <SI key={f.k} label={f.l} value={phys[f.k]}
                      onChange={v => setP(f.k, v)} min={f.min} max={f.max} step={f.step} unit={f.u} />
                  ))}
                </div>
              ))}
              <button onClick={() => setPhys(DEF_PHYS)} style={{
                padding:"10px", background:"transparent",
                border:`1px solid ${T.b2}`, borderRadius: 6,
                color: T.t1, cursor:"pointer", fontFamily: FONT.sans,
                fontSize: 12, fontWeight: 500, transition:"border-color 0.12s",
              }}>
                Reset to defaults
              </button>
            </div>

            <div style={{ display:"flex", flexDirection:"column", gap: 16 }}>
              <div style={{ ...CARD }}>
                <SectionHead>Live Orbital Mechanics</SectionHead>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap: 8 }}>
                  {[
                    { l:"Orbital radius r",  v:orb.r.toFixed(0),              u:"m",              c:T.acc  },
                    { l:"Orbital velocity v",v:orb.v.toFixed(2),              u:"m/s",            c:T.acc  },
                    { l:"Period T",          v:orb.T_orb.toFixed(0),          u:"s",              c:T.t0   },
                    { l:"Atmos density ρ",   v:(orb.rho*1e13).toFixed(3),     u:"×10⁻¹³ kg/m³",  c:T.yel  },
                    { l:"Frontal area",      v:(orb.area*1e4).toFixed(0),     u:"cm²",            c:T.t0   },
                    { l:"Drag force",        v:(orb.Fd*1e6).toFixed(3),       u:"μN",             c:T.yel  },
                    { l:"Drag power",        v:(orb.Fd*orb.v*1000).toFixed(1),u:"mW",             c:T.red  },
                    { l:"In-track ΔV/day",   v:(orb.dv_intrack/miss.years/365).toFixed(4),u:"m/s/day",c:T.grn},
                    { l:"SRP force",         v:(orb.srp_force*1e9).toFixed(2),u:"nN",             c:T.pur  },
                    { l:"J2 precession",     v:orb.J2_precession.toFixed(4),  u:"°/day",          c:T.pur  },
                    { l:"Eclipse fraction",  v:(orb.eclipse/orb.T_orb*100).toFixed(1),u:"%",      c:T.t1   },
                    { l:"Ground track/day",  v:orb.gs_day.toFixed(0),         u:"Mm/day",         c:T.t0   },
                  ].map(s => <MetricCard key={s.l} label={s.l} value={s.v} unit={s.u} color={s.c} />)}
                </div>
              </div>

              <div style={{ ...CARD }}>
                <SectionHead color={T.grn}>EP-ADCS Cycle Physics</SectionHead>
                {[
                  { l:"Momentum debt per 133 s",        v:(orb.Fd*133).toFixed(4),                            u:"mNs",     max:0.01, c:T.acc  },
                  { l:"Required ΔV per pulse",          v:(orb.Fd*133/miss.mass*1000).toFixed(4),             u:"mm/s",    max:0.5,  c:T.t0   },
                  { l:"Propellant per pulse (EP-ADCS)", v:(orb.Fd*133/miss.mass/(miss.isp*9.81)*1e6).toFixed(2),u:"μg",   max:20,   c:T.grn  },
                  { l:"EMMS absorption stroke",         v:Math.min(32,orb.Fd*133/0.8*1000).toFixed(2),        u:"mm / 32", max:32,   c:T.acc  },
                  { l:"Energy leverage",                v:"52",                                                u:"×",       max:60,   c:T.yel  },
                  { l:"Overshoot factor η",             v:"1.020",                                             u:"",        max:2,    c:T.grn  },
                ].map(s => (
                  <Gauge key={s.l} label={s.l} value={Number(s.v)} max={s.max} color={s.c} unit={s.u} />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ══ EP-ADCS TAB ═══════════════════════════════════ */}
        {tab === "propulsion" && (
          <div style={{ display:"flex", flexDirection:"column", gap: 16 }}>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap: 16 }}>
              {[
                { title:"EMMS Unit Specification", color:SC.adcs, items:[
                  { l:"Units (SKM-4)",        v:"4 ×",                                      sub:"3-axis + redundancy" },
                  { l:"Stroke",               v:"±32 mm",                                   sub:"linear track" },
                  { l:"Peak force",           v:"800 mN",                                   sub:"absorption" },
                  { l:"Reset force",          v:"10 mN",                                    sub:"return stroke" },
                  { l:"Bandwidth",            v:"8 Hz",                                     sub:"electromagnetic" },
                  { l:"Response time",        v:"< 20 ms",                                  sub:"coil lag" },
                  { l:"Residual jitter",      v:"< 5 μrad",                                 sub:"pointing stability" },
                  { l:"Track repeatability",  v:"< 5 μm",                                   sub:"THK linear guide" },
                  { l:"Coil inductance",      v:"2 mH" },
                  { l:"Coil resistance",      v:"0.5 Ω",                                    sub:"τ = 4 ms" },
                  { l:"Moving mass",          v:"1 kg × 4",                                 sub:"total 4 kg" },
                  { l:"Average power",        v:"4 mW",                                     sub:"per unit" },
                ]},
                { title:"Cold Gas Thruster", color:SC.prop, items:[
                  { l:"Unit",                 v:"VACCO MiPS",                               sub:"M18199-01" },
                  { l:"Propellant",           v:"N₂ (nitrogen)" },
                  { l:"Isp",                  v:`${miss.isp} s`,                            sub:`vₑ = ${(miss.isp*9.81).toFixed(0)} m/s` },
                  { l:"Nominal thrust",       v:"5 mN",                                     sub:"4 nozzles" },
                  { l:"Min impulse bit",      v:"0.4 μNs",                                  sub:"PWM capable" },
                  { l:"Valve response",       v:"0.6 ms" },
                  { l:"Tank MEOP",            v:"3000 psi",                                 sub:"COPV CFRP" },
                  { l:"Propellant load",      v:`${(orb.prop_ep*1000).toFixed(0)} g`,       sub:"EP-ADCS budget" },
                  { l:"Conv. baseline (5yr)", v:`${orb.prop_conv.toFixed(3)} kg` },
                  { l:"EP-ADCS load (5yr)",   v:`${orb.prop_ep.toFixed(3)} kg` },
                  { l:"Saving",               v:`${orb.saving_pct.toFixed(1)} %`,           sub:`${(orb.prop_conv-orb.prop_ep).toFixed(3)} kg` },
                  { l:"Pulse interval",       v:"133 s" },
                ]},
                { title:"Power Budget", color:SC.eps, items:[
                  { l:"EMMS active (peak)",   v:"800 mW",                                   sub:"all 4 units firing" },
                  { l:"EMMS average",         v:"16 mW",                                    sub:"0.96% duty cycle" },
                  { l:"EMMS controller",      v:"120 mW",                                   sub:"continuous" },
                  { l:"Valve driver",         v:"50 mW",                                    sub:"pulsed" },
                  { l:"Total average",        v:"220 mW",                                   sub:"EP-ADCS system" },
                  { l:"Total peak",           v:"970 mW",                                   sub:"during correction" },
                  { l:"Solar available",      v:`${pw.p_solar.toFixed(1)} W`,               sub:"at orbit" },
                  { l:"Total consumption",    v:`${pw.p_total_sun.toFixed(1)} W`,           sub:"all subsystems" },
                  { l:"Eclipse consumption",  v:`${pw.p_total_eclipse.toFixed(1)} W`,       sub:"no payload/tx" },
                  { l:"Battery capacity",     v:`${pw.bat_capacity.toFixed(1)} Wh`,         sub:"30% DoD" },
                  { l:"Battery mass",         v:`${(pw.bat_mass*1000).toFixed(0)} g` },
                  { l:"Power margin",         v:`${(pw.p_solar-pw.p_total_sun).toFixed(1)} W`, sub:"sunlight" },
                ]},
              ].map(col => (
                <div key={col.title} style={{ ...CARD, borderTop:`2px solid ${col.color}60`, padding:"18px 20px" }}>
                  <SectionHead color={col.color}>{col.title}</SectionHead>
                  {col.items.map(item => (
                    <SpecRow key={item.l} label={item.l} value={item.v} sub={item.sub} color={col.color} />
                  ))}
                </div>
              ))}
            </div>

            <div style={{ ...CARD }}>
              <SectionHead color={SC.prop}>
                Propellant Saving Mechanisms — {miss.years}-Year Mission @ {miss.alt} km
              </SectionHead>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap: 12 }}>
                {[
                  { id:"W1", label:"In-track retrograde elimination",   η:"1.639 → 1.000", save:(orb.prop_conv-orb.prop_conv/1.639).toFixed(3), pct:"39", c:T.grn },
                  { id:"W3", label:"Oscillatory perturbation absorption",η:"× 0.90",       save:(orb.prop_conv/1.639*0.10).toFixed(3),           pct:"10", c:T.acc },
                  { id:"W4", label:"Desaturation burns eliminated",     η:"−0.08 kg/yr",   save:(0.08*miss.years*miss.mass/100).toFixed(3),      pct:"~3", c:T.pur },
                  { id:"W5", label:"CoM recalibration eliminated",      η:"trim",          save:(0.35*miss.mass/100).toFixed(3),                 pct:"~3", c:T.yel },
                  { id:"W6", label:"Day-night drag sync",               η:"× 0.92",        save:(orb.prop_conv/1.639*0.90*0.08).toFixed(3),      pct:"8",  c:T.org },
                ].map(w => (
                  <div key={w.id} style={{ ...CARD_INNER, borderTop:`2px solid ${w.c}60` }}>
                    <div style={{ fontFamily: FONT.mono, fontSize: 11, fontWeight: 700, color: w.c, marginBottom: 6 }}>{w.id}</div>
                    <div style={{ fontFamily: FONT.sans, fontSize: 12, color: T.t0, lineHeight: 1.5, marginBottom: 8 }}>{w.label}</div>
                    <div style={{ fontFamily: FONT.mono, fontSize: 11, color: T.t2, marginBottom: 6 }}>{w.η}</div>
                    <div style={{ fontFamily: FONT.mono, fontSize: 16, fontWeight: 700, color: w.c }}>−{w.save} kg</div>
                    <div style={{ fontFamily: FONT.sans, fontSize: 10, color: T.t2, marginTop: 2 }}>−{w.pct}% of baseline</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ══ SUBSYSTEMS TAB ════════════════════════════════ */}
        {tab === "subsystems" && (
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap: 16 }}>
            {[
              { key:"structure", title:"Structure", color:SC.struct, icon:"⬛", specs:[
                `Bus: ${miss.satClass} CubeSat Al 6061-T6`,
                `Total mass budget: ${miss.mass} kg`,
                `Structure mass: ${(miss.mass*0.08).toFixed(2)} kg (~8%)`,
                "Primary fasteners: M3×8 Ti Grade 5 (Würth)",
                "Secondary: M2.5×6 A4 SS (Würth) / M2×5 A4 SS (Bossard)",
                "Inserts: Heli-Coil M3 1.5D stainless",
                "Separation: ISIS PPOD / NanoRacks NRCSD",
                "Surface: Black anodise Type II + MAP space black paint",
              ]},
              { key:"obc", title:"Onboard Computer", color:SC.obc, icon:"💻", specs:[
                "Primary OBC: GomSpace NanoMind Z7000",
                "CPU: Xilinx Zynq-7000 dual ARM Cortex-A9 @ 866 MHz",
                "RAM: 512 MB DDR3 · Storage: 8 GB eMMC + 32 GB mass mem",
                "OS: FreeRTOS + GomSpace libGS",
                "Protocol: CSP (CubeSat Space Protocol) on CAN/I2C/SPI",
                "FDIR: Watchdog (MAX706) + independent reset",
                "RTC: DS3231 ±2 ppm with backup battery",
                "Radiation tolerance: 20 krad TID",
              ]},
              { key:"eps", title:"Electrical Power", color:SC.eps, icon:"⚡", specs:[
                "PMDU: EnduroSat EPS Type III",
                `Solar: ${miss.satClass} deployable + body panels`,
                `Solar power: ${pw.p_solar.toFixed(1)} W (orbit average)`,
                `Battery: GomSpace NanoPower BPX ${pw.bat_capacity.toFixed(0)} Wh`,
                `Battery mass: ${(pw.bat_mass*1000).toFixed(0)} g · DoD: 30%`,
                "Rails: 3.3 V / 5 V / 12 V regulated",
                `Sunlight: ${pw.p_total_sun.toFixed(1)} W · Eclipse: ${pw.p_total_eclipse.toFixed(1)} W`,
                "Battery heaters: Minco Thermofoil 5 W ×2",
              ]},
              { key:"comms", title:"Communications", color:SC.comms, icon:"📡", specs:[
                "TT&C: ISIS TRXUV UHF/VHF half-duplex",
                "Uplink: VHF 144–148 MHz @ 9.6 kbps AX.25/CSP",
                "Downlink TT&C: UHF 400–403 MHz @ 38.4 kbps",
                "Payload DL: Syrlinks EWC27 S-band 2.25 GHz @ 10 Mbps",
                "UHF antenna: ISIS tape spring turnstile (deployable)",
                "S-band antenna: Patch RHCP 6 dBi",
                "AES-256 encryption on S-band payload link",
              ]},
              { key:"adcs", title:"ADCS / EP-ADCS", color:SC.adcs, icon:"🎯", specs:[
                "Attitude: EP-ADCS EMMS SKM-4 (4-axis, no reaction wheels)",
                "Jitter: < 5 μrad RMS · Response: < 0.2 s",
                "Star tracker: BST ST-200 (< 5 arcsec, 8°×8° FOV)",
                "Magnetometer: Billingsley TFM100G2 ±100 μT",
                "Sun sensors: Tensor Tech FSS-15 ×6 faces",
                "GPS: Novatel OEM719 L1/L2 (< 1.5 m CEP)",
                "IMU: Analog Devices ADIS16490 6-DOF",
                "Cold gas (station keeping): VACCO MiPS N₂",
              ]},
              { key:"thermal", title:"Thermal Control", color:SC.thermal, icon:"🌡", specs:[
                "Strategy: Passive primary + active heaters for battery",
                "MLI: Sheldahl 15-layer blanket (ε < 0.03)",
                "Thermal tape: 3M 850 (α = 0.12, ε = 0.04)",
                "Black paint: AkzoNobel MAP (α = 0.96, ε = 0.88)",
                "Battery heaters: Minco HK5578 5 W ×2",
                "Survival heaters: Minco silicone 3 W ×4",
                "TIM: Bergquist GP3000 3 W/m·K",
                "Op range: −20°C to +60°C · Survival: −40°C to +70°C",
              ]},
              { key:"payload", title:`Payload — ${miss.payload}`, color:SC.payload, icon:"📷", specs:[
                "Unit: Simera Sense MultiScape100 CIS",
                `Mass: ${miss.payloadMass} kg · Power: ${miss.payloadPower} W`,
                `GSD: ~${(miss.alt*0.011).toFixed(1)} m from ${miss.alt} km`,
                "Interface: MIPI CSI-2 4-lane to OBC FPGA",
                "Compression: JPEG2000 onboard",
                "Mount: Ti-6Al-4V kinematic 3-point",
                "Pointing req: < 0.1° (EP-ADCS provides < 0.003°)",
                "Thermal: 0°C to +50°C operating",
              ]},
              { key:"harness", title:"Harness & Connectors", color:SC.harness, icon:"🔌", specs:[
                "Power bus: 22 AWG PTFE silver-plated (Alpha Wire 5857)",
                "Signal bus: 28 AWG twisted pair PTFE (Belden 9841)",
                "RF coax: Huber+Suhner MXSS-1.13 U.FL→SMA",
                "Connectors: Glenair MicroD-15 hermetic (MIL-DTL-83513)",
                "Bus: Samtec SSW-120 PC-104 stack",
                "Power: Molex Micro-Fit 3.0 8-circuit (8 A rated)",
                "Crimp: Positronic Nano-Miniature gold contacts",
                "Total harness mass: ~150 g (estimated)",
              ]},
            ].map(sub => (
              <div key={sub.key} style={{ ...CARD, borderTop:`2px solid ${sub.color}50` }}>
                <div style={{ display:"flex", gap: 10, alignItems:"center", marginBottom: 16 }}>
                  <span style={{ fontSize: 20 }}>{sub.icon}</span>
                  <div style={{ fontFamily: FONT.sans, fontSize: 13, fontWeight: 700,
                    color: sub.color, letterSpacing:"0.03em" }}>{sub.title}</div>
                </div>
                {sub.specs.map((spec, i) => (
                  <div key={i} style={{ display:"flex", gap: 10, padding:"5px 0",
                    borderBottom:`1px solid ${T.b0}` }}>
                    <span style={{ color: sub.color, fontSize: 9, marginTop: 3, flexShrink: 0 }}>▸</span>
                    <span style={{ fontFamily: FONT.sans, fontSize: 12, color: T.t1, lineHeight: 1.6 }}>{spec}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* ══ FULL BOM TAB ══════════════════════════════════ */}
        {tab === "bom" && (
          <>
            {/* Summary tiles */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap: 10, marginBottom: 20 }}>
              {[
                { l:"Line Items",       v:allBOM.length,                u:"items", c:T.acc  },
                { l:"Total Components", v:totalItems,                   u:"units", c:T.t0   },
                { l:"Total Mass",       v:totalMass.toFixed(2),         u:"kg",    c:T.yel  },
                { l:"Total Power",      v:totalPow.toFixed(1),          u:"W",     c:T.grn  },
                { l:"Est. BOM Cost",    v:`$${(totalCost/1000).toFixed(0)}k`, u:"USD", c:T.pur },
              ].map(stat => (
                <div key={stat.l} style={{ ...CARD, borderTop:`2px solid ${stat.c}50`, padding:"14px 16px" }}>
                  <div style={{ ...LABEL_XS, marginBottom: 4 }}>{stat.l}</div>
                  <div style={{ fontFamily: FONT.mono, fontSize: 22, fontWeight: 700, color: stat.c, lineHeight: 1 }}>
                    {stat.v}
                    <span style={{ fontFamily: FONT.sans, fontSize: 11, color: T.t2, marginLeft: 4, fontWeight: 400 }}>{stat.u}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Subsystem filter */}
            <div style={{ display:"flex", gap: 6, marginBottom: 14, flexWrap:"wrap" }}>
              {["all", ...Object.keys(BOM)].map(f => (
                <button key={f} onClick={() => setBomFilter(f)} style={{
                  padding:"5px 14px", cursor:"pointer", fontFamily: FONT.sans, fontSize: 11,
                  fontWeight: bomFilter === f ? 600 : 400,
                  background: bomFilter === f ? `${T.acc}15` : "transparent",
                  border: `1px solid ${bomFilter === f ? T.acc : T.b1}`,
                  color: bomFilter === f ? T.acc : T.t2, borderRadius: 6,
                  textTransform:"capitalize", transition:"all 0.12s",
                }}>
                  {f} ({f === "all" ? allBOM.length : BOM[f]?.length || 0})
                </button>
              ))}
            </div>

            {/* BOM table */}
            <div style={{ ...CARD, padding: 0, overflow:"hidden", marginBottom: 16 }}>
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead>
                    <tr>
                      {["ID","Subsystem","Component","Vendor / Part No","Qty","Mass (g)","Power (mW)","Cost ($)","Specification"].map(h => (
                        <TH key={h}>{h}</TH>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtBOM.map((item, i) => {
                      const sc = subColors[item.subsys] || T.t0;
                      return (
                        <tr key={item.id} style={{ background: i%2===0 ? T.bg1 : T.bg2 }}>
                          <TD mono c={sc}>{item.id}</TD>
                          <TD>
                            <span style={{ ...chip(sc) }}>{item.subsys}</span>
                          </TD>
                          <TD bold>{item.name}</TD>
                          <TD>
                            <div style={{ fontFamily: FONT.sans, fontSize: 12, color: T.t1 }}>{item.vendor}</div>
                            <div style={{ fontFamily: FONT.mono, fontSize: 10, color: sc, marginTop: 2 }}>{item.pn}</div>
                          </TD>
                          <TD mono>{item.qty}</TD>
                          <TD mono c={T.yel}>{(item.mass_g * item.qty).toFixed(1)}</TD>
                          <TD mono c={item.power_mw > 0 ? T.acc : T.t2}>{item.power_mw > 0 ? (item.power_mw * item.qty).toFixed(0) : "—"}</TD>
                          <TD mono c={T.pur}>${(item.cost_usd * item.qty).toFixed(0)}</TD>
                          <TD><span style={{ fontFamily: FONT.sans, fontSize: 11, color: T.t2, lineHeight: 1.5 }}>{item.spec}</span></TD>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Per-subsystem breakdown */}
            <div style={{ ...CARD }}>
              <SectionHead>Mass & Cost by Subsystem</SectionHead>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap: 10 }}>
                {Object.entries(BOM).map(([k, items]) => {
                  const m_sub = items.reduce((s, b) => s + b.mass_g * b.qty, 0) / 1000;
                  const c_sub = items.reduce((s, b) => s + b.cost_usd * b.qty, 0);
                  const color = subColors[items[0]?.subsys] || T.t0;
                  const pct = (m_sub / totalMass * 100);
                  return (
                    <div key={k} style={{ ...CARD_INNER }}>
                      <div style={{ fontFamily: FONT.sans, fontSize: 11, fontWeight: 600,
                        color, marginBottom: 8, textTransform:"capitalize" }}>{k}</div>
                      <div style={{ fontFamily: FONT.mono, fontSize: 16, fontWeight: 700, color }}>{m_sub.toFixed(3)} kg</div>
                      <div style={{ fontFamily: FONT.sans, fontSize: 10, color: T.t2, marginBottom: 8 }}>{pct.toFixed(1)}% of total</div>
                      <div style={{ ...BAR_TRACK, marginBottom: 8 }}>
                        <div style={{ height:"100%", width:`${pct}%`, background: color, borderRadius: 3 }} />
                      </div>
                      <div style={{ fontFamily: FONT.sans, fontSize: 11, color: T.t2 }}>${(c_sub/1000).toFixed(1)}k</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* ══ SEQUENCE TAB ══════════════════════════════════ */}
        {tab === "sequence" && (
          <>
            {/* Timeline strip */}
            <div style={{ ...CARD, marginBottom: 16 }}>
              <SectionHead>Mission Timeline — {miss.name} · {miss.years}-Year Mission</SectionHead>
              <div style={{ display:"flex", alignItems:"center", overflowX:"auto", paddingBottom: 4, gap: 0 }}>
                {SEQ.map((phase, i) => (
                  <div key={i} style={{ display:"flex", alignItems:"center", flexShrink: 0 }}>
                    <button onClick={() => setSeqOpen(seqOpen === i ? null : i)} style={{
                      background: seqOpen === i ? `${phase.color}18` : "transparent",
                      border: `1px solid ${seqOpen === i ? phase.color : T.b1}`,
                      borderRadius: 6, padding:"8px 14px", cursor:"pointer",
                      textAlign:"center", minWidth: 120, transition:"all 0.12s",
                    }}>
                      <div style={{ fontFamily: FONT.sans, fontSize: 9, fontWeight: 700,
                        color: phase.color, letterSpacing:"0.08em", textTransform:"uppercase", marginBottom: 3 }}>
                        {phase.phase}
                      </div>
                      <div style={{ fontFamily: FONT.sans, fontSize: 10, color: T.t1, lineHeight: 1.4 }}>{phase.title}</div>
                      <div style={{ fontFamily: FONT.mono, fontSize: 9, color: T.t2, marginTop: 4 }}>{phase.t}</div>
                    </button>
                    {i < SEQ.length - 1 && (
                      <div style={{ width: 20, height: 1, background: T.b2, flexShrink: 0 }} />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Phase cards */}
            {SEQ.map((phase, i) => (
              <div key={i} style={{ ...CARD, borderLeft:`3px solid ${phase.color}`,
                marginBottom: 10, padding:"16px 20px", transition:"box-shadow 0.15s",
                boxShadow: seqOpen === i ? `0 0 0 1px ${phase.color}40` : "none" }}>
                <button onClick={() => setSeqOpen(seqOpen === i ? null : i)}
                  style={{ width:"100%", background:"none", border:"none", cursor:"pointer",
                    display:"flex", justifyContent:"space-between", alignItems:"center", padding: 0 }}>
                  <div style={{ display:"flex", gap: 12, alignItems:"center" }}>
                    <span style={{ ...chip(phase.color) }}>{phase.phase}</span>
                    <span style={{ fontFamily: FONT.sans, fontSize: 14, fontWeight: 600, color: T.t0 }}>{phase.title}</span>
                    <span style={{ fontFamily: FONT.mono, fontSize: 11, color: T.t2 }}>{phase.t}</span>
                  </div>
                  <span style={{ fontFamily: FONT.sans, fontSize: 11, color: T.t2 }}>
                    {seqOpen === i ? "▲ collapse" : "▼ expand"}
                  </span>
                </button>

                {seqOpen === i && (
                  <div style={{ marginTop: 16, paddingTop: 16, borderTop:`1px solid ${T.b1}` }}>
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap: 8 }}>
                      {phase.steps.map((step, si) => (
                        <div key={si} style={{ display:"flex", gap: 12, ...CARD_INNER, alignItems:"flex-start" }}>
                          <div style={{ width: 22, height: 22, borderRadius:"50%",
                            background: `${phase.color}18`, border:`1px solid ${phase.color}50`,
                            display:"flex", alignItems:"center", justifyContent:"center",
                            flexShrink: 0, fontFamily: FONT.mono, fontSize: 9,
                            fontWeight: 700, color: phase.color }}>
                            {si + 1}
                          </div>
                          <span style={{ fontFamily: FONT.sans, fontSize: 12, color: T.t1, lineHeight: 1.6 }}>{step}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </>
        )}

        {/* ══ SUMMARY TAB ═══════════════════════════════════ */}
        {tab === "summary" && (
          <>
            {/* Mission identity banner */}
            <div style={{ ...CARD, marginBottom: 16, borderTop:`3px solid ${T.acc}` }}>
              <div style={{ fontFamily: FONT.sans, fontSize: 10, fontWeight: 600,
                letterSpacing:"0.1em", textTransform:"uppercase", color: T.t2, marginBottom: 6 }}>
                Mission Datasheet · Revision A
              </div>
              <div style={{ fontFamily: FONT.sans, fontSize: 26, fontWeight: 700, color: T.t0, marginBottom: 4 }}>
                {miss.name}
              </div>
              <div style={{ fontFamily: FONT.sans, fontSize: 13, color: T.t1, marginBottom: 16 }}>
                {miss.org} · {miss.satClass} CubeSat · {miss.alt} km {miss.inc}° SSO · {miss.years}-year mission · EP-ADCS station keeping
              </div>
              <div style={{ display:"flex", gap: 8, flexWrap:"wrap" }}>
                {[
                  { l:"Class",      v:miss.satClass,           c:T.yel    },
                  { l:"Orbit",      v:`${miss.alt} km SSO`,    c:T.acc    },
                  { l:"Isp",        v:`${miss.isp} s`,         c:T.grn    },
                  { l:"Propulsion", v:"EP-ADCS SKM-4",         c:T.grn    },
                  { l:"Payload",    v:miss.payload,            c:SC.payload},
                  { l:"Duration",   v:`${miss.years} years`,   c:T.t0    },
                ].map(tag => (
                  <span key={tag.l} style={{ ...chip(tag.c), fontSize: 11 }}>{tag.l}: {tag.v}</span>
                ))}
              </div>
            </div>

            {/* Mass & power budgets */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap: 16, marginBottom: 16 }}>

              {/* Mass budget */}
              <div style={{ ...CARD }}>
                <SectionHead color={T.yel}>Mass Budget</SectionHead>
                {[
                  { l:"Structure",            v:(miss.mass*0.08).toFixed(2),                                            c:SC.struct  },
                  { l:"OBC",                  v:(0.09+0.015+0.005+0.003).toFixed(3),                                    c:SC.obc     },
                  { l:"EPS (panels + bat.)",  v:(pw.bat_mass+pw.solar_mass).toFixed(3),                                 c:SC.eps     },
                  { l:"Comms",                v:(0.075+0.150+0.045+0.028).toFixed(3),                                   c:SC.comms   },
                  { l:"ADCS (EMMS + sensors)",v:(4*0.5+0.290+0.038+0.006*6+0.018+0.016).toFixed(3),                    c:SC.adcs    },
                  { l:"Propulsion",           v:(BOM.prop.reduce((s,b)=>s+b.mass_g*b.qty/1000,0)).toFixed(3),           c:SC.prop    },
                  { l:"Thermal",              v:(BOM.thermal.reduce((s,b)=>s+b.mass_g*b.qty/1000,0)).toFixed(3),        c:SC.thermal },
                  { l:"Payload",              v:miss.payloadMass.toFixed(3),                                            c:SC.payload },
                  { l:"Harness",              v:"0.150",                                                                c:SC.harness },
                  { l:"Propellant (EP-ADCS)", v:orb.prop_ep.toFixed(3),                                                c:T.grn      },
                ].map(row => {
                  const val = parseFloat(row.v);
                  const pct = (val / miss.mass * 100).toFixed(1);
                  return (
                    <div key={row.l} style={{ marginBottom: 10 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom: 4 }}>
                        <span style={{ fontFamily: FONT.sans, fontSize: 12, color: T.t1 }}>{row.l}</span>
                        <div style={{ display:"flex", gap: 14, alignItems:"baseline" }}>
                          <span style={{ fontFamily: FONT.sans, fontSize: 10, color: T.t2 }}>{pct}%</span>
                          <span style={{ fontFamily: FONT.mono, fontSize: 12, fontWeight: 600,
                            color: row.c, minWidth: 60, textAlign:"right" }}>{row.v} kg</span>
                        </div>
                      </div>
                      <div style={{ ...BAR_TRACK }}>
                        <div style={{ height:"100%", width:`${Math.min(parseFloat(pct),100)}%`,
                          background: row.c, borderRadius: 3 }} />
                      </div>
                    </div>
                  );
                })}
                <div style={{ borderTop:`1px solid ${T.b1}`, marginTop: 12, paddingTop: 10,
                  display:"flex", justifyContent:"space-between", alignItems:"baseline" }}>
                  <span style={{ fontFamily: FONT.sans, fontSize: 12, color: T.t1 }}>Total (estimated)</span>
                  <span style={{ fontFamily: FONT.mono, fontSize: 18, fontWeight: 700, color: T.yel }}>
                    {miss.mass.toFixed(2)} kg</span>
                </div>
              </div>

              {/* Power budget */}
              <div style={{ ...CARD }}>
                <SectionHead color={T.acc}>Power Budget</SectionHead>
                {[
                  { l:"OBC",              sun:pw.p_obc,         ecl:pw.p_obc,         c:SC.obc     },
                  { l:"EPS (PMDU)",       sun:pw.p_eps,         ecl:pw.p_eps,         c:SC.eps     },
                  { l:"Comms RX",         sun:pw.p_comms_rx,    ecl:pw.p_comms_rx,    c:SC.comms   },
                  { l:"Comms TX",         sun:pw.p_comms_tx,    ecl:0,                c:SC.comms   },
                  { l:"ADCS",             sun:pw.p_adcs,        ecl:pw.p_adcs,        c:SC.adcs    },
                  { l:"Thermal",          sun:pw.p_thermal*0.3, ecl:pw.p_thermal,     c:SC.thermal },
                  { l:"Propulsion",       sun:pw.p_prop,        ecl:pw.p_prop,        c:SC.prop    },
                  { l:"Payload",          sun:pw.p_payload,     ecl:0,                c:SC.payload },
                ].map(row => {
                  const maxP = pw.p_total_sun;
                  return (
                    <div key={row.l} style={{ marginBottom: 10 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom: 4 }}>
                        <span style={{ fontFamily: FONT.sans, fontSize: 12, color: T.t1 }}>{row.l}</span>
                        <div style={{ display:"flex", gap: 12 }}>
                          <span style={{ fontFamily: FONT.mono, fontSize: 10, color: T.yel }}>
                            Sun {row.sun.toFixed(1)} W</span>
                          <span style={{ fontFamily: FONT.mono, fontSize: 10, color: T.t2 }}>
                            Ecl {row.ecl.toFixed(1)} W</span>
                        </div>
                      </div>
                      <div style={{ display:"flex", gap: 4 }}>
                        <div style={{ flex:1, ...BAR_TRACK }}>
                          <div style={{ height:"100%", width:`${(row.sun/maxP)*100}%`,
                            background:row.c, borderRadius:3 }} />
                        </div>
                        <div style={{ flex:1, ...BAR_TRACK }}>
                          <div style={{ height:"100%", width:`${(row.ecl/maxP)*100}%`,
                            background:row.c, opacity:0.4, borderRadius:3 }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div style={{ borderTop:`1px solid ${T.b1}`, marginTop: 12, paddingTop: 10 }}>
                  {[
                    { l:"Sunlight total",  v:`${pw.p_total_sun.toFixed(1)} W`,   c:T.yel  },
                    { l:"Eclipse total",   v:`${pw.p_total_eclipse.toFixed(1)} W`,c:T.t1   },
                    { l:"Solar available", v:`${pw.p_solar.toFixed(1)} W`,        c:T.grn  },
                  ].map(r => (
                    <div key={r.l} style={{ display:"flex", justifyContent:"space-between",
                      alignItems:"baseline", marginBottom: 6 }}>
                      <span style={{ fontFamily: FONT.sans, fontSize: 12, color: T.t1 }}>{r.l}</span>
                      <span style={{ fontFamily: FONT.mono, fontSize: 14, fontWeight: 700, color: r.c }}>{r.v}</span>
                    </div>
                  ))}
                  <div style={{ marginTop: 8, padding:"10px 12px", borderRadius: 6,
                    background: pw.p_solar > pw.p_total_sun ? `${T.grn}10` : `${T.red}10`,
                    border:`1px solid ${pw.p_solar > pw.p_total_sun ? T.grn : T.red}40` }}>
                    <span style={{ fontFamily: FONT.sans, fontSize: 12, fontWeight: 600,
                      color: pw.p_solar > pw.p_total_sun ? T.grn : T.red }}>
                      {pw.p_solar > pw.p_total_sun
                        ? `Power positive — +${(pw.p_solar - pw.p_total_sun).toFixed(1)} W margin`
                        : `Power deficit — −${(pw.p_total_sun - pw.p_solar).toFixed(1)} W · reduce payload duty cycle`}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* KPP table */}
            <div style={{ ...CARD, marginBottom: 16 }}>
              <SectionHead color={T.grn}>Key Performance Parameters — EP-ADCS vs Conventional</SectionHead>
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead>
                    <tr>
                      {["Parameter","Conventional","EP-ADCS (this mission)","Improvement"].map(h => (
                        <TH key={h} c={h === "EP-ADCS (this mission)" ? T.grn : TH_STYLE.color}>{h}</TH>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { p:"Propellant mass",        conv:`${orb.prop_conv.toFixed(3)} kg`,                              ep:`${orb.prop_ep.toFixed(3)} kg`,                      delta:`−${orb.saving_pct.toFixed(0)}%`,                           good:true  },
                      { p:"Overshoot factor η",     conv:"1.923",                                                       ep:"1.020",                                              delta:"47% closer to ideal",                                      good:true  },
                      { p:"Attitude jitter (RMS)",  conv:"800–1200 μrad (RW)",                                          ep:"< 5 μrad",                                           delta:"160× improvement",                                         good:true  },
                      { p:"Station keeping cadence",conv:"Every 15–90 min",                                             ep:"Every 133 s",                                        delta:`${(900/133).toFixed(0)}× more frequent`,                   good:true  },
                      { p:"Desaturation burns/year",conv:"25–50 burns",                                                 ep:"Zero",                                               delta:"Eliminated",                                               good:true  },
                      { p:"System power",           conv:"17 W (RW + cold gas)",                                        ep:"0.22 W (EP-ADCS)",                                   delta:"77× lower draw",                                           good:true  },
                      { p:"Reaction wheels",        conv:"YES — 3-axis set",                                            ep:"NO — EMMS replaces",                                 delta:"−0.5 kg, no bearing failure risk",                         good:true  },
                      { p:"Mission life",           conv:`${miss.years} years`,                                         ep:`${(orb.prop_conv/orb.prop_ep*miss.years).toFixed(1)} years`, delta:`+${(orb.prop_conv/orb.prop_ep*miss.years-miss.years).toFixed(1)} yr extension`, good:true },
                    ].map((row, i) => (
                      <tr key={row.p} style={{ background: i%2===0 ? T.bg1 : T.bg2 }}>
                        <TD>{row.p}</TD>
                        <TD c={T.red}>{row.conv}</TD>
                        <TD c={T.grn} bold mono>{row.ep}</TD>
                        <TD c={row.good ? T.grn : T.t2}>{row.delta}</TD>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Cost / Schedule / Risk */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap: 16, marginBottom: 16 }}>

              {/* Cost */}
              <div style={{ ...CARD }}>
                <SectionHead color={T.pur}>Estimated Cost Breakdown</SectionHead>
                {Object.entries(BOM).map(([k, items]) => {
                  const color = subColors[items[0]?.subsys] || T.t0;
                  const v = items.reduce((s, b) => s + b.cost_usd * b.qty, 0);
                  return (
                    <div key={k} style={{ display:"flex", justifyContent:"space-between",
                      alignItems:"baseline", padding:"6px 0", borderBottom:`1px solid ${T.b0}` }}>
                      <span style={{ fontFamily: FONT.sans, fontSize: 12, color: T.t1, textTransform:"capitalize" }}>{k}</span>
                      <span style={{ fontFamily: FONT.mono, fontSize: 12, fontWeight: 600, color }}>${(v/1000).toFixed(1)}k</span>
                    </div>
                  );
                })}
                <div style={{ borderTop:`1px solid ${T.b1}`, marginTop: 10, paddingTop: 10 }}>
                  {[
                    { l:"Hardware BOM",    v:`$${(totalCost/1000).toFixed(0)}k`,            c:T.pur, sz:14 },
                    { l:"AIT + test (~40%)", v:`$${(totalCost*0.4/1000).toFixed(0)}k`,       c:T.t2, sz:11 },
                    { l:"Launch (~30%)",   v:`$${(totalCost*0.3/1000).toFixed(0)}k`,         c:T.t2, sz:11 },
                  ].map(r => (
                    <div key={r.l} style={{ display:"flex", justifyContent:"space-between",
                      alignItems:"baseline", marginBottom: 4 }}>
                      <span style={{ fontFamily: FONT.sans, fontSize: r.sz === 11 ? 11 : 12, color: T.t1 }}>{r.l}</span>
                      <span style={{ fontFamily: FONT.mono, fontSize: r.sz, fontWeight: r.sz===14?700:400, color: r.c }}>{r.v}</span>
                    </div>
                  ))}
                  <div style={{ marginTop: 10, padding:"10px 12px",
                    background:`${T.pur}10`, border:`1px solid ${T.pur}30`, borderRadius: 6 }}>
                    <div style={{ fontFamily: FONT.sans, fontSize: 10, color: T.t2, marginBottom: 2 }}>Total Mission Cost (est.)</div>
                    <div style={{ fontFamily: FONT.mono, fontSize: 20, fontWeight: 700, color: T.pur }}>
                      ${(totalCost*1.7/1000000).toFixed(1)}M USD</div>
                  </div>
                </div>
              </div>

              {/* Schedule */}
              <div style={{ ...CARD }}>
                <SectionHead color={T.org}>Schedule (Indicative)</SectionHead>
                {[
                  { phase:"Phase 0 — Feasibility",    dur:"3 months",         c:T.t2  },
                  { phase:"Phase A — Concept",         dur:"4 months",         c:T.t1  },
                  { phase:"Phase B — Prelim Design",   dur:"5 months",         c:T.t1  },
                  { phase:"Phase C — Detailed Design", dur:"6 months",         c:T.acc },
                  { phase:"Phase D — AIT",             dur:"8 months",         c:T.yel },
                  { phase:"Phase E — Launch + Ops",    dur:`${miss.years} yr`, c:T.grn },
                  { phase:"Phase F — Deorbit",         dur:"3 months",         c:T.red },
                ].map(s => (
                  <div key={s.phase} style={{ display:"flex", justifyContent:"space-between",
                    alignItems:"center", padding:"8px 0", borderBottom:`1px solid ${T.b0}` }}>
                    <div style={{ display:"flex", gap: 10, alignItems:"center" }}>
                      <div style={{ ...dot(s.c) }} />
                      <span style={{ fontFamily: FONT.sans, fontSize: 12, color: T.t1 }}>{s.phase}</span>
                    </div>
                    <span style={{ fontFamily: FONT.mono, fontSize: 12, fontWeight: 600, color: s.c }}>{s.dur}</span>
                  </div>
                ))}
                <div style={{ marginTop: 12, padding:"10px 12px",
                  background:`${T.org}10`, border:`1px solid ${T.org}30`, borderRadius: 6 }}>
                  <div style={{ fontFamily: FONT.sans, fontSize: 10, color: T.t2, marginBottom: 2 }}>Total Development</div>
                  <div style={{ fontFamily: FONT.mono, fontSize: 16, fontWeight: 700, color: T.org }}>~26 months to launch</div>
                </div>
              </div>

              {/* Risk register */}
              <div style={{ ...CARD }}>
                <SectionHead>Risk Register</SectionHead>
                {[
                  { risk:"EMMS coil derating in vacuum",    sev:"M", prob:"L", mit:"Thermal vacuum qualification + pre-charge logic",          c:T.yel },
                  { risk:"Cold gas valve lifetime",         sev:"M", prob:"L", mit:"VACCO valve rated 10M cycles vs 1.18M required",           c:T.yel },
                  { risk:"Star tracker blinding",           sev:"H", prob:"M", mit:"Software exclusion zones + sun sensor fallback",           c:T.org },
                  { risk:"Battery degradation (EOL)",       sev:"M", prob:"M", mit:"Conservative DoD 30%, heater control TBD",                c:T.yel },
                  { risk:"Payload pointing requirement",    sev:"L", prob:"L", mit:"EP-ADCS 5 μrad far exceeds 0.1° requirement",             c:T.grn },
                  { risk:"Launch orbit miss (>50 km)",      sev:"H", prob:"L", mit:"Hohmann transfer budget in prop reserve",                 c:T.org },
                  { risk:"UHF antenna non-deployment",      sev:"H", prob:"L", mit:"Redundant burn wire + SMA body antenna backup",           c:T.org },
                  { risk:"SEU in OBC causing reboot",       sev:"M", prob:"M", mit:"Watchdog + FreeRTOS safe state + weekly scrub",           c:T.yel },
                ].map(row => (
                  <div key={row.risk} style={{ marginBottom: 8, ...CARD_INNER,
                    borderLeft:`3px solid ${row.c}`, padding:"8px 12px" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom: 4 }}>
                      <span style={{ fontFamily: FONT.sans, fontSize: 12, color: T.t0, fontWeight: 500 }}>{row.risk}</span>
                      <div style={{ display:"flex", gap: 4 }}>
                        <span style={{ ...chip(row.c), fontSize: 9 }}>S:{row.sev}</span>
                        <span style={{ ...chip(T.t2), fontSize: 9 }}>P:{row.prob}</span>
                      </div>
                    </div>
                    <div style={{ fontFamily: FONT.sans, fontSize: 11, color: T.t2, lineHeight: 1.5 }}>{row.mit}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Standards */}
            <div style={{ ...CARD }}>
              <SectionHead>Standards & Compliance</SectionHead>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap: 12 }}>
                {[
                  { cat:"Mechanical", color:SC.struct, items:["ECSS-E-ST-32 Structures","MIL-STD-1540 Launch loads","GEVS GSFC-STD-7000A vibration","IPC Class 3 PCB assembly"] },
                  { cat:"Electrical", color:SC.eps,    items:["ECSS-E-ST-20 EPS","MIL-STD-461 EMC","IPC-4552 surface finish","MIL-DTL-83513 connectors"] },
                  { cat:"Software",   color:SC.obc,    items:["ECSS-E-ST-40 SW engineering","CSP CubeSat protocol standard","FreeRTOS MISRA-C compliant"] },
                  { cat:"Comms",      color:SC.comms,  items:["ITU-R S.1002 frequency coord","AX.25 APRS protocol","CCSDS telemetry packet standard","AES-256 S-band encryption"] },
                  { cat:"Orbital",    color:T.grn,     items:["IADC 25-year deorbit rule","FCC orbital debris guidelines",`${miss.alt} km — natural decay ${miss.alt>500?"25yr":"<10yr"}`,"Post-mission passivation required"] },
                  { cat:"Quality",    color:T.pur,     items:["ECSS-Q-ST-70 materials","ISO 9001 / AS9100D suppliers","MIL-STD-1246 contamination","GSFC-STD-7000 acceptance testing"] },
                ].map(group => (
                  <div key={group.cat} style={{ ...CARD_INNER }}>
                    <div style={{ fontFamily: FONT.sans, fontSize: 11, fontWeight: 700,
                      color: group.color, marginBottom: 10, textTransform:"uppercase",
                      letterSpacing:"0.06em" }}>
                      {group.cat}
                    </div>
                    {group.items.map((item, i) => (
                      <div key={i} style={{ display:"flex", gap: 8, marginBottom: 5 }}>
                        <span style={{ color: group.color, fontSize: 10, marginTop: 1, flexShrink: 0 }}>✓</span>
                        <span style={{ fontFamily: FONT.sans, fontSize: 11, color: T.t1, lineHeight: 1.5 }}>{item}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

      </div>
    </div>
  );
}
